import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  Input,
  output,
  type OnDestroy,
  type OnChanges,
  OnInit,
  type SimpleChanges,
  signal,
  viewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { headerLocationBadge } from '@modules/common/utils/header-location-badge';
import { WorkbookPreviewComponent } from '@modules/sheets/workbook-preview.component';
import { SheetVersionHistoryLazyComponent } from '@modules/sheets/components/sheet-version-history/sheet-version-history-lazy.component';
import { templateCountLabels, workbookCountLabels, workbookCounts } from '@modules/sheets/workbook-counts';
import { createFromTemplateAndOpen, openInEditorFullWindow } from '@modules/sheets/new-workbook';
import { openWorkbookInEditor } from '@modules/sheets/open-workbook';
import { engineVersion, formatStorage, formulaCellCount, workbookOwnerName, workbookStatus } from '@modules/sheets/workbook-details';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { type Space } from '@modules/settings/types/space';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { Application } from '@modules/common/types/application';
import { type FullWindowStart, injectFullWindowStart } from '@modules/common/services/full-window.service';
import { NgTemplateOutlet } from '@angular/common';
import { APP_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { DetailBreadcrumbsComponent, type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import { TitleBySheetsFilterPipe } from '@modules/sheets/pipes/title-by-sheets-filter.pipe';
import { ContainerBadgeComponent } from '@modules/common/components/container-badge/container-badge.component';
import { SheetFolderSummariesCache } from '@modules/sheets/services/sheet-folder-summaries.cache';
import { type AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { InputComponent } from '@frontendlib/components/controls/input/input.component';
import { SearchLocationBadgeComponent } from '@frontendlib/components/components/search-location-badge/search-location-badge.component';
import { CommentsModule } from '@modules/comments/comments.module';
import { FeatureUpsellComponent } from '@modules/common/components/feature-upsell/feature-upsell.component';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { ItemDescriptionFieldComponent } from '@modules/common/components/item-description/item-description-field.component';
import { StitchTimestampsComponent } from '@modules/common/components/stitch-timestamps/stitch-timestamps.component';
import { TabsComponent } from '@modules/common/components/tabs/tabs.component';
import { PreventDirtyClickDirective } from '@modules/common/directives/prevent-dirty-click.directive';
import { Tab } from '@modules/common/types/tab';
import { Upload } from '@modules/common/types/upload';
import { ConnectionsComponent } from '@modules/connections/components/connections/connections.component';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { LinkedInfoListComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list.component';
import { EntityHubSearchComponent } from '@modules/elements/components/entity-hub/entity-hub-search/entity-hub-search.component';
import { StitchHubComponent } from '@modules/elements/components/stitch-hub/stitch-hub.component';
import { LinkedInfoListShortComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list-short.component';
import { type ListState as StitchListState } from '@modules/linked-info/types/list-state';
import { SaveCancelPanelComponent } from '@modules/elements/components/stitch-save-cancel-panel/stitch-save-cancel-panel.component';
import { SheetContextMenuDirective } from '@modules/elements/directives/sheet-context-menu.directive';
import { ElementsModule } from '@modules/elements/elements.module';
import { AutocompleteInputComponent } from '@modules/form-controls/components/autocomplete-input/autocomplete-input.component';
import { ButtonComponent } from '@modules/form-controls/components/button/button.component';
import { type ActionsMenuAction } from '@modules/form-controls/components/editor/tools/actions-menu.tool';
import { QuickActionComponent } from '@modules/form-controls/components/quick-action/quick-action.component';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { KnotsComponent } from '@modules/knots/components/knots/knots.component';
import { KnotsService } from '@modules/knots/services/knots.service';
import { Knot } from '@modules/knots/types/knot';
import { KnowledgeHubComponent } from '@modules/knowledge/components/knowledge-hub/knowledge-hub.component';
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SheetDetailTabRequests } from '@modules/sheets/services/sheet-detail-tab-requests';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { Sheet, type SheetFormGroupConfig } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { ScrollModule } from '@modules/scroll/scroll.module';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { TagsComponent } from '@modules/tags/components/tags/tags.component';
import { TagsService } from '@modules/tags/services/tags.service';
import { Tag } from '@modules/tags/types/tag';
import { ResizeDirective } from '@modules/utils/directives/resize/resize.directive';
import { ToArrayPipe } from '@modules/utils/pipes/array/to-array.pipe';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { defer, map, startWith, type Subscription, take, takeUntil } from 'rxjs';
import { CommonModule as AppCommonModule } from '../../../common/common.module';
import { EditorChrome, type ItemTabs, WORKBOOK_FORM_TABS } from '../../editor-chrome';
import { EditorSaveStatus } from '../../editor-save-status';
import { SheetsExpandedHeaderComponent } from '../sheets-expanded-header/sheets-expanded-header.component';
import { fill, SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';

@Component({
  selector: 'app-sheet-form',
  templateUrl: './sheet-form.component.html',
  styleUrls: ['./sheet-form.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    DetailBreadcrumbsComponent,
    ContainerBadgeComponent,
    SearchLocationBadgeComponent,
    ReactiveFormsModule,
    MatTooltipModule,
    AppCommonModule,
    CommentsModule,
    DragNDropModule,
    ElementsModule,
    FormControlsModule,
    ContextMenuStateIndicatorComponent,
    ScrollModule,
    InputComponent,
    QuickActionComponent,
    Button2Component,
    ButtonComponent,
    IconComponent,
    PreventDirtyClickDirective,
    SheetContextMenuDirective,
    TabsComponent,
    StitchTimestampsComponent,
    TagsComponent,
    KnotsComponent,
    ConnectionsComponent,
    AutocompleteInputComponent,
    ItemDescriptionFieldComponent,
    LinkedInfoListComponent,
    EntityHubSearchComponent,
    StitchHubComponent,
    LinkedInfoListShortComponent,
    SaveCancelPanelComponent,
    KnowledgeHubComponent,
    FeatureUpsellComponent,
    ToArrayPipe,
    ResizeDirective,
    WorkbookPreviewComponent,
    SheetVersionHistoryLazyComponent,
    DateFormatPipe,
    AsyncPipe,
    SheetsExpandedHeaderComponent,
  ],
  host: { '[class.in-editor]': 'inEditor()' },
  // Its tabs, for its header's "…" menu: in the workbook's own window, Add comment opens Comments here (SBUX-1107).
  providers: [
    {
      provide: WORKBOOK_FORM_TABS,
      useFactory: (form: SheetFormComponent): ItemTabs => ({
        id: () => form.sheet?.id,
        current: () => form.selectedTab,
        select: (tab) => form.selectTab(tab),
      }),
      deps: [forwardRef(() => SheetFormComponent)],
    },
  ],
})
export class SheetFormComponent
  extends FullFormBaseComponent<StitchType.SHEET, Sheet, SheetFormGroupConfig>
  implements OnInit, OnChanges, OnDestroy
{
  /** NFR-8: Appendix F's strings, from their one definition. */
  protected readonly S = SHEETS_STRINGS;
  /** Which sheet-folder the open item is filed under; see {@link headerLocationBadge}. */
  protected readonly locationBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.sheetFolderId,
    cache: inject(SheetFolderSummariesCache),
    search: inject(AdvancedSearchService),
    application: Application.sheets,
    badgeType: 'sheet-folder',
    alsoWhen: this.openedFromHub,
  });

  private readonly sheetRevision = itemChangeRevision(this.stitchItemChanged);
  private readonly titleBySheetsFilter = new TitleBySheetsFilterPipe();
  /** stitch-app-shell breadcrumbs, as in every app. */
  protected readonly navigation = new DetailNavigation(SheetsAppStateService, {
    appLabel: APP_CRUMBS.sheets,
    viewTitle: (folder) => this.titleBySheetsFilter.transform(folder, null),
  });

  /** Sheets app › the list's view › this sheet. */
  protected breadcrumbs(): DetailCrumb[] {
    this.sheetRevision();
    return this.navigation.crumbs({
      container: this.headerFolder()?.title,
      fallbackView: this.titleBySheetsFilter.transform('all_sheets'),
      itemTitle: this.sheet.title || UNTITLED_CRUMB,
      itemId: this.sheet?.id,
    });
  }

  /** The folder this sheet sits in, named by the header's container badge. */
  protected readonly headerFolder = inject(SheetFolderSummariesCache).get(
    computed(() => {
      this.sheetRevision();
      return this.stitchItem?.sheetFolderId ?? null;
    }),
  );

  protected readonly highlights = signal<string[]>([]);
  /** §4 and §11.5.3: the title rule's message under the field (`workbookTitleValidator`); empty keeps "Add title". */
  protected readonly titleMessages = { workbookTitle: SHEETS_STRINGS['Workbook titles are 1–200 characters.'] };
  public changesKey = 'sheet';
  /**
   * The prototype's item tabs (Oleg, 2026-10-07: tab names as the prototype), except the grid's, which says
   * "Workbook" (Johanna, 2026-10-29; it was the Design Lab's "Sheets"). Values are unchanged: `sheet` is still the grid's tab.
   */
  public tabs: Tab[] = [
    { title: SHEETS_STRINGS.Workbook, value: 'sheet' },
    { title: 'Details', value: 'details' },
    { title: 'Hub', value: 'hub' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
    // SBUX-1146: version history, as Slides' History tab.
    { title: SHEETS_STRINGS.History, value: 'history' },
  ];
  /**
   * The workbook as the HEADER sees it — a new object every time the item changes (SBUX-1043, q53).
   *
   * `FullFormBaseComponent.applyUpdatedItem` merges a server update with
   * `Object.assign(this.stitchItem, updatedItem)`: in place, same reference. Everything that reads
   * `sheet` straight from this template re-reads it on the next check and is correct —
   * "Last updated", the Details tab's own title field, the revision. `SheetsExpandedHeaderComponent`
   * does not: it takes the workbook through a signal `input()`, whose equality is `Object.is`, so an
   * unchanged reference means none of its `computed`s ever run again and the title and its avatar
   * initials stay on the name the workbook had when the header was built.
   *
   * Seen in the app before this existed: the same workbook open in the main editor and the fourth
   * panel, renamed in the main editor — the panel's Details tab showed "Renamed in main q53" and its
   * meta row showed the new minute, while the header above both still read "Department Budget".
   *
   * The clone keeps the prototype: `menuItem` hands this object to the item context menu, which
   * expects a real `Sheet`, not a bag of its fields.
   */
  protected readonly headerSheet = signal<Sheet | null>(null);

  private publishHeaderSheet(): void {
    const sheet = this.sheet;
    this.headerSheet.set(
      sheet?.id ? (Object.assign(Object.create(Object.getPrototypeOf(sheet) as object), sheet) as Sheet) : null,
    );
  }

  protected override attachmentsMenuStateVariant = 'sheets';
  public readonly editorActions: ActionsMenuAction[];

  protected dragDataType = DragDataTypes.sheet;

  private readonly sheetFoldersService = inject(SheetFoldersService);
  public suggestions: AutocompleteFactory<SheetFolder> = this.sheetFoldersService.getAutocompleteSuggestions();

  @Input() sheet = new Sheet();

  /**
   * SBUX-1036: this form is the editor's page (`/sheets/open`), stretched across the list and detail columns: the item
   * header and tabs, as the prototype's editor has them, with the editor projected in as the Sheets tab. The other tabs
   * show here in place of the grid, which stays mounted.
   */
  public readonly inEditor = input(false);

  /**
   * SBUX-1043: where a workbook this form creates (today: "+ Use template") should open. Unset means
   * the editor's route, which is the Sheets page's editor and the right answer everywhere but the
   * fourth panel, which opens it in itself.
   */
  public readonly openCreated = input<((sheet: Sheet) => void) | null>(null);
  /**
   * SBUX-1141 R04: set by an item window, where "Edit workbook", a sheet button and "Open" in the "…" menu open the
   * workbook's cells in the window instead of taking the person to Sheets (Wook: "Clicking it should open the sheet in
   * the modal"). A workbook not saved yet is saved first, as Save saves it (Wook: "Create it, open cells").
   */
  public readonly openInWindow = input<((sheet: Sheet, sheetId?: string) => void) | null>(null);
  /** Create from → Sheets: the window asks for the cells at once, so its new workbook is made and opened (Wook: like
   * Slides, Create from this item should open the new workbook straight in the editor). */
  public readonly openOnStart = input(false);
  /**
   * The editor's "Show stitched items" (the prototype's row-2 toggle): the Tags, Knots and Connections lists, closed
   * until asked for, so the grid keeps its height. The library's own toggle, shared across items, is left as it is.
   */
  protected readonly editorKnowledgeOpen = signal(false);
  protected knowledgeHidden(): boolean {
    return this.inEditor() ? !this.editorKnowledgeOpen() : !this.displayKnowledge;
  }
  private readonly editorChrome = inject(EditorChrome);
  private readonly editorSaveStatus = inject(EditorSaveStatus);
  private readonly editorSurface = viewChild<ElementRef<HTMLElement>>('editorSurface');
  /** Where the grid was when another tab hid it; it stays there, at its size (see `selectTab`). */
  protected editorTop = 0;
  private detachTabs?: () => void;

  /**
   * The prototype's "<n> sheets" in the dates row: the editor's own count, a sheet just added included; until it has
   * loaded, the stored workbook's.
   */
  protected readonly sheetCountLabel = computed(() => {
    const n = this.editorSaveStatus.sheets() ?? workbookCounts(this.previewSnapshot())?.sheets;
    if (n === undefined || n === null) return null;
    return n === 1 ? fill('1 sheet') : fill('{n} sheets', { n: n.toLocaleString('en-US') });
  });

  /**
   * A tab picked, by its tab, `switch_item_tab` or the item menu's "Add comment". Leaving the grid's tab, the grid
   * keeps its place and size behind the others, and gives up keyboard focus, so typing on Details can't reach a cell.
   */
  public selectTab(tab: string): boolean {
    if (!this.tabs.some((t) => t.value === tab)) return false;
    const surface = this.editorSurface()?.nativeElement;
    if (surface && this.selectedTab === 'sheet' && tab !== 'sheet') {
      this.editorTop = surface.offsetTop;
      const focused = document.activeElement as HTMLElement | null;
      if (focused && surface.contains(focused)) focused.blur();
    }
    this.selectedTab = tab;
    this.changeDetector.markForCheck();
    this.tabChanged.emit(tab);
    return true;
  }

  /**
   * The tab that is now showing. `selectTab` is the only way one changes — the tab row, the UI
   * command `switch_item_tab` and the item menu's "Add comment" all come through here — so a host
   * that has to remember which tab the user was on can bind this and nothing else.
   *
   * SBUX-1043: the fourth panel does, because the DP destroys it on every app switch and the
   * handoff counts the tab among the panel state that must survive one.
   */
  public readonly tabChanged = output<string>();

  /**
   * SBUX-1043: "Open in main app", the fourth panel's only way into the full editor.
   *
   * Emitted rather than acted on, because the host owns both halves the form does not: whether the
   * grid may be flushed first, and which worksheet is showing. The panel answers both.
   */
  public readonly openInMain = output<void>();

  private readonly router = inject(Router);
  /** SBUX-1145: Edit workbook and Use template open the workbook full-window, in the Sheets app's main view only. */
  private readonly fullWindowStart = injectFullWindowStart(Application.sheets);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly sheetsService = inject(SheetsService);
  private readonly detailTabRequests = inject(SheetDetailTabRequests);

  /**
   * The workbook for the preview, fetched on open.
   *
   * The list is requested with `short=true`, so its rows carry no snapshot -- a
   * list of workbooks must not ship a workbook each. The detail view is the one
   * place that needs the cells, so it asks for them here rather than making
   * every row pay.
   *
   * Null while in flight and for an unsaved item; the preview renders nothing
   * rather than a stale workbook from the previously selected row.
   */
  protected readonly previewSnapshot = signal<object | null>(null);
  /** §11.5.3's Storage, from the same details read as the snapshot (`storageBytes`); null until it answers. */
  protected readonly previewStorage = signal<number | null>(null);
  /** §11.5.2: "<n> sheets | <cell_count> cells | <k> tables" beside "Edit workbook"; a template's, "<n> sheets | Template". */
  protected readonly countLabels = computed(() => {
    const counts = workbookCounts(this.previewSnapshot());
    return counts ? (this.sheet?.template ? templateCountLabels(counts) : workbookCountLabels(counts)) : [];
  });
  private previewRequest?: Subscription;

  /**
   * Gap row 29: §11.5.3's counts and engine version, from the snapshot the preview loads. Null until it has
   * loaded; the tab then shows only what the item itself carries.
   */
  protected readonly facts = computed(() => {
    const snapshot = this.previewSnapshot();
    const counts = workbookCounts(snapshot);
    if (!counts) return null;
    const n = (v: number) => v.toLocaleString('en-US');
    const version = engineVersion(snapshot);
    return {
      sheets: n(counts.sheets),
      cells: n(counts.cells),
      formulas: n(formulaCellCount(snapshot)),
      engine: version ? fill('Spreadsheet engine {version}', { version }) : null,
    };
  });

  /** §11.5.3's Storage, formatted as the pack says; null until the details read has answered. */
  protected readonly storage = computed(() => {
    const bytes = this.previewStorage();
    return bytes === null ? null : formatStorage(bytes);
  });

  /**
   * The Design Lab's read-only Owner on Details: the user who made the workbook, named from the space's participants
   * (Wook, 2026-10-07; {@link workbookOwnerName}). The space is asked only when Details shows it (`defer`), so the
   * other tabs never wait on it; the name is read from the workbook shown now, so another workbook names its own.
   */
  private readonly spacesService = inject(SpacesService);
  protected readonly space$ = defer(() => this.spacesService.getCurrentSpace());
  protected owner(space: Space | null | undefined): string | null {
    return workbookOwnerName(space, this.sheet?.createdBy);
  }

  /** The Design Lab's Status on Details: where the workbook is, as the form has it now (§12). */
  protected status(): string {
    return workbookStatus({ deleted: this.form?.controls.deleted.value, archived: this.form?.controls.archived.value });
  }

  /** Gap row 26: how many items are stitched to the workbook, beside "Connected context". */
  protected readonly connectedCount = computed(() => this.visibleLinkedInfo().length);
  /** The prototype's "Connected context" lists every stitched item: none of the Stitch tab's filters apply. */
  protected readonly connectedListState: StitchListState = { filters: {}, sort: { by: 'date', order: 'desc' }, view: 'short' };

  public override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if ('sheet' in changes) {
      this.publishHeaderSheet();
      this.loadPreviewSnapshot();
      // On the first change `ngOnInit` hasn't picked the opening tab yet; it takes the request itself.
      if (!changes['sheet'].firstChange) this.takeRequestedTab();
    }
  }

  /**
   * A tab asked for from outside, such as Comments by the item menu's "Add comment" (gap row 58), once this view
   * shows that workbook. Only the main view takes it: the Dynamic Panel, a dialog and the Knowledge Hub show
   * their own copy of a workbook and stay where they are.
   */
  private takeRequestedTab(): void {
    if (!this.showCloseButton || this.isDynamicPanel || this.openedFromHub()) return;
    const tab = this.detailTabRequests.take(this.sheet?.id);
    if (tab) this.selectTab(tab);
  }

  private loadPreviewSnapshot(): void {
    this.previewRequest?.unsubscribe();
    this.previewSnapshot.set(null);
    this.previewStorage.set(null);

    const id = this.sheet?.id;
    if (!id) {
      return;
    }
    // Already carrying one (a freshly saved item, or a details read) -- no round trip.
    if (this.sheet.snapshot) {
      this.previewSnapshot.set(this.sheet.snapshot);
      this.previewStorage.set(this.sheet.storageBytes ?? null);
      return;
    }

    this.previewRequest = this.sheetsService
      .getItem(id)
      .pipe(take(1))
      .subscribe({
        next: (full) => {
          this.previewSnapshot.set(full?.snapshot ?? null);
          this.previewStorage.set(full?.storageBytes ?? null);
        },
        // A preview that will not load shows the empty state, not an error: the
        // workbook itself is still openable.
        error: () => this.previewSnapshot.set(null),
      });
  }

  /**
   * The details' "Edit workbook" button: in the Sheets app it opens the workbook full-window (SBUX-1145), once the
   * editor's route is reached. In a window the cells open there, and the Dynamic Panel's opens as it always has. The
   * sheet buttons under the preview and the header's "…" → Open go straight to `expand` and fold nothing.
   */
  protected openWorkbook(): void {
    this.expand(undefined, this.fullWindowStart);
  }

  /**
   * Expand the workbook across the list and detail columns.
   *
   * A route rather than a flag, so an open workbook is linkable and Back closes
   * it -- and so the workbook chunk stays lazy until someone actually opens one.
   *
   * "Edit workbook", a sheet button under the preview, which opens on that sheet (AM-109), and "Open" in the
   * header's "…" menu, which opens the editor as the prototype's does (gap row 39).
   */
  protected expand(sheetId?: string, start: FullWindowStart | null = null): void {
    const inWindow = this.openInWindow();
    if (inWindow) {
      this.openHere(inWindow, sheetId);
      return;
    }
    if (!this.sheet?.id) return;
    // From the detail dialog, the dialog closes: it stayed open over the editor (live pass, 2026-10-03).
    // In a dialog there is no close button (`showCloseButton`), which is how the form knows it is in one.
    if (this.showCloseButton === false) this.handleClose();
    const opening = openWorkbookInEditor(this.router, this.sheet.id, sheetId);
    if (start) void this.startOnArrival(opening, start);
  }

  /** Starts full-window once the editor's route is reached; a navigation refused or overtaken starts nothing. */
  private async startOnArrival(opening: Promise<boolean>, start: FullWindowStart): Promise<void> {
    if (await opening) start();
  }

  /** In a window, the cells open there (SBUX-1141 R04); a workbook not saved yet is saved first. */
  private openHere(open: (sheet: Sheet, sheetId?: string) => void, sheetId?: string): void {
    if (this.sheet?.id) {
      open(this.sheet, sheetId);
      return;
    }
    // The save gives this form's workbook its id in place.
    this.handleSubmit()
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        if (this.sheet?.id) open(this.sheet, sheetId);
      });
  }

  /**
   * The Trash banner's "Restore" (S-13), as the editor's banner and the menu's "Restore From Trash" do it. The
   * workbook goes back where it was (§12 transition 6); the banner goes once the form says it is out of Trash.
   */
  protected restoreFromTrash(): void {
    if (!this.sheet?.id) return;
    this.sheetsService
      .delete({ ids: [this.sheet.id] }, false)
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.sheet.deleted = false;
        this.form.controls.deleted.setValue(false, { emitEvent: false });
        this.changeDetector.markForCheck();
      });
  }

  /**
   * Gap row 25: the description under the preview saves when it loses focus, as the prototype's textarea does
   * (`onBlur={e=>updateItem(current.id,{description:e.target.value})}`), rather than waiting for Save. It goes through
   * the form's own save, so whatever else waits in the form goes with it: the prototype saves each field as it is
   * left, so that is what it would have kept. Not while the form can't be saved (a title the rule refuses): Save
   * says why. The Details tab's description keeps the form's Save, as its Title and Folder do.
   */
  protected saveDescriptionOnBlur(): void {
    if (!this.sheet?.id || !this.form?.controls.description.dirty || this.form.invalid) return;
    // The list's card shows the description (`Sheet.shouldRefreshList`).
    this.handleSubmit(true);
  }

  /**
   * "Use template", where a workbook's view has "Edit workbook" (Wook, 2026-10-06, the Design Lab's detail view): a
   * new workbook from this template, opened in the editor, as "From a template" makes one.
   */
  protected useTemplate(): void {
    if (!this.sheet?.id) return;
    if (this.showCloseButton === false) this.handleClose();
    // SBUX-1043: a host that has somewhere of its own to open the new workbook says so, and then the
    // library is left alone too. From the fourth panel both of those matter: without them, using a
    // template there navigated the whole app to the editor's route and reset the SECTION's library —
    // two main-app effects from a side panel, which is what `OpenOptions.open` exists to avoid.
    const open = this.openCreated();
    // Gap row 10, as New Workbook: inside Sheets, the library shows its root list, unsearched, behind the editor.
    const library = !open && this.sheetsAppState ? { search: this.advancedSearchService, folders: this.sheetsAppState } : null;
    void createFromTemplateAndOpen(
      { sheetsService: this.sheetsService, router: this.router, library, open: open ?? openInEditorFullWindow(this.router, this.fullWindowStart) },
      { template: this.sheet.id },
    ).catch(() => undefined);
  }

  private readonly advancedSearchService = inject(AdvancedSearchService);
  /** Sheets' sidebar state: provided inside Sheets only, as the nav bar's New Workbook finds it. */
  private readonly sheetsAppState = inject(SheetsAppStateService, { optional: true });
  private readonly tagsService = inject(TagsService);
  private readonly knotsService = inject(KnotsService);

  constructor() {
    super(inject(Injector), inject(SheetsService));
    // Create from → Sheets (SBUX-1141 R04): once the form is up, its workbook is made and its cells open in the window.
    afterNextRender(() => {
      if (this.openOnStart() && this.openInWindow()) this.expand();
    });

    this.editorActions = [
      { label: 'Search', handler: (text: string) => this.handleSearch(text) },
      { label: 'Make Tag', handler: (text: string) => this.handleMakeTag(text) },
      { label: 'Make Knot', handler: (text: string) => this.handleMakeKnot(text) },
    ];
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    // A revision saved elsewhere (the editor, another tab, Stitchy) arrives as UPDATED, and the base
    // form merges it into `sheet` in place. OnPush cannot see a mutation, so "Updated …" kept the
    // old time and the preview the old grid until a reload (B-112).
    this.itemUpdated.pipe(takeUntil(this.alive)).subscribe(() => {
      this.loadPreviewSnapshot();
      // `markForCheck` is enough for the bindings that read `sheet` straight from the template, but
      // not for the header: it takes the workbook through a signal `input()`, and the merge above
      // keeps the same object, so nothing it computes ever re-runs (SBUX-1043, q53).
      this.publishHeaderSheet();
      this.changeDetector.markForCheck();
    });

    // After the base picked the opening tab: a request made before this view existed wins over it.
    this.takeRequestedTab();
    if (this.inEditor()) {
      this.detachTabs = this.editorChrome.attachItemTabs({
        id: () => this.sheet?.id,
        current: () => this.selectedTab,
        select: (tab) => this.selectTab(tab),
      });
    }
    this.detailTabRequests.requested.pipe(takeUntil(this.alive)).subscribe(() => this.takeRequestedTab());

    this.kpService
      .getSelectedKnots()
      .pipe(
        startWith([]),
        map((knots) => knots.map((k) => k.name)),
        takeUntil(this.alive),
      )
      .subscribe((highlights) => this.highlights.set(highlights));
  }

  public override ngOnDestroy(): void {
    this.detachTabs?.();
    super.ngOnDestroy();
  }

  /**
   * In the editor, "Move to Trash" keeps the workbook open, read-only under its banner, as the item menu's does there
   * and the prototype's editor does; in the library the detail view closes, as before. Deleting for good closes both.
   */
  public override delete(): void {
    if (!this.inEditor() || !this.sheet?.id || this.stitchItem.deleted) {
      super.delete();
      return;
    }
    this.sheetsService
      .delete({ ids: [this.sheet.id] }, true)
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.sheet.deleted = true;
        this.form.controls.deleted.setValue(true, { emitEvent: false });
        this.changeDetector.markForCheck();
      });
  }

  public handleEditorUploadedAttachment(upload: Upload): void {
    this.form.controls['inlineUploads'].setValue(this.form.controls['inlineUploads'].value.concat(upload));
  }

  get stitchItem(): Sheet {
    return this.sheet;
  }

  private handleSearch(text: string): void {
    if (!text) {
      return;
    }
    this.advancedSearchService.search(text);
  }

  private handleMakeTag(text: string): void {
    if (!text) {
      return;
    }
    const tag = new Tag({ name: text, pinned: false, added: true });
    if (this.sheet?.id) {
      this.tagsService.upsertBulk([tag], [this.sheet], true, true).subscribe();
    } else {
      this.addToControl(this.form.controls['tags'], tag);
    }
  }

  private handleMakeKnot(text: string): void {
    if (!text) {
      return;
    }
    const knot = new Knot({ name: text, pinned: false, added: true });
    if (this.sheet?.id) {
      this.knotsService.upsertBulk([knot], [this.sheet], true, true).subscribe();
    } else {
      this.addToControl(this.form.controls['knots'], knot);
    }
  }

  private addToControl<T extends Tag | Knot>(control: AbstractControl<T[] | null>, item: T): void {
    const current: T[] = control.value || [];
    if (current.some((existing) => Tag.normalizeName(existing.name) === Tag.normalizeName(item.name))) {
      return;
    }
    control.setValue([...current, item]);
    control.markAsDirty();
    control.markAsTouched();
  }

  protected shouldRefreshList(prev, current): boolean {
    return Sheet.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Sheet {
    return Sheet.fromFormGroup(this.form);
  }

  protected asFormGroup(): FormGroup<SheetFormGroupConfig> {
    return this.sheet.asFormGroup({ maxAttachmentSize: this.maxUploadSize() });
  }
}
